// ============================================================================
// worldmap.js — the Conquest overview screen. A painted realm map with clickable
// regions (coloured by the faction that holds them, crowned if a seat, ringed and
// counting down if under invasion), adjacency lines, a side panel to inspect a
// region and act on it (march there yourself, raise an army under a general and
// dispatch him, or ride through your own lands), a war-standings readout, a day
// clock that ticks the enemy pressure forward, and an event log. Pure DOM over
// the canvas; it calls back into the GameManager to launch battles.
// ============================================================================

import { FACTIONS, ENEMY_FACTIONS } from '../game/conquest.js';

const DAY_MS = 13000;  // real-time ms per campaign day while the map is open (deliberately slow/deliberate)

const CSS = `
#cq { position:fixed; inset:0; z-index:58; display:none; pointer-events:auto;
  font-family:'Trebuchet MS',system-ui,sans-serif; color:#e6ddd2;
  background:radial-gradient(circle at 42% 40%, rgba(14,9,6,.72), rgba(3,2,2,.96)); }
#cq.show { display:flex; }
#cq-left { flex:1; display:flex; align-items:center; justify-content:center; min-width:0; position:relative; }
#cq-map { position:relative; width:min(82vh,60vw); aspect-ratio:1/1;
  background:#0a0806 center/cover no-repeat; border:1px solid rgba(255,106,26,.3);
  box-shadow:0 0 60px rgba(0,0,0,.7), inset 0 0 90px rgba(0,0,0,.6); }
#cq-edges { position:absolute; inset:0; width:100%; height:100%; pointer-events:none; }
.cq-node { position:absolute; transform:translate(-50%,-50%); cursor:pointer; text-align:center; pointer-events:auto; }
.cq-dot { width:26px; height:26px; border-radius:50%; margin:0 auto; border:2px solid #0c0a08;
  box-shadow:0 0 0 2px rgba(0,0,0,.5), 0 2px 6px #000; transition:transform .12s; }
.cq-node:hover .cq-dot { transform:scale(1.25); }
.cq-node.sel .cq-dot { box-shadow:0 0 0 3px #ffb257, 0 0 16px rgba(255,178,87,.9); }
.cq-node.seat .cq-dot { width:32px; height:32px; border-width:3px; }
.cq-lbl { font-size:11px; font-weight:700; letter-spacing:.04em; margin-top:2px; text-shadow:0 1px 3px #000,0 0 6px #000; white-space:nowrap; }
.cq-gar { font-size:10px; color:#c9c0b4; text-shadow:0 1px 2px #000; }
.cq-crown { position:absolute; top:-14px; left:50%; transform:translateX(-50%); font-size:12px; }
.cq-threat { position:absolute; inset:-9px; border-radius:50%; border:2px solid #ff3b3b; animation:cqpulse 1s infinite; pointer-events:none; }
.cq-tdays { position:absolute; top:-13px; right:-16px; font-size:10px; font-weight:900; color:#ff5a5a; text-shadow:0 1px 2px #000; }
@keyframes cqpulse { 0%,100%{opacity:.35;transform:scale(1)} 50%{opacity:1;transform:scale(1.12)} }

#cq-side { width:min(360px,32vw); flex:0 0 auto; background:rgba(6,4,3,.82); border-left:1px solid rgba(255,106,26,.25);
  padding:18px 18px 14px; display:flex; flex-direction:column; overflow-y:auto; }
#cq-side h1 { font-size:22px; font-weight:900; letter-spacing:.14em; color:#d7dbe0; margin:0; }
#cq-day { color:#ff8a3a; letter-spacing:.24em; font-size:12px; margin:3px 0 12px; }
.cq-stand { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:12px; }
.cq-stand .f { display:flex; align-items:center; gap:5px; font-size:12px; color:#cabfb2; }
.cq-swatch { width:11px; height:11px; border-radius:2px; display:inline-block; border:1px solid rgba(0,0,0,.5); }
#cq-detail { border:1px solid rgba(255,106,26,.22); background:rgba(12,8,6,.7); padding:12px; margin-bottom:12px; min-height:96px; }
#cq-detail .rn { font-size:17px; font-weight:900; color:#ffb257; letter-spacing:.04em; }
#cq-detail .ro { font-size:11px; letter-spacing:.14em; text-transform:uppercase; margin:2px 0 6px; }
#cq-detail .rd { font-size:12.5px; color:#c2b9ac; line-height:1.5; }
#cq-detail .rstat { font-size:12px; color:#e6ddd2; margin-top:8px; }
#cq-detail .warn { color:#ff7a6a; font-weight:700; margin-top:6px; font-size:12.5px; }
.cq-btn { pointer-events:auto; cursor:pointer; margin:5px 0; padding:9px 12px; text-align:center;
  border:1px solid rgba(255,106,26,.4); background:rgba(10,7,5,.7); color:#ffb257; letter-spacing:.1em;
  font-size:12.5px; font-weight:700; transition:all .14s; }
.cq-btn:hover { background:rgba(255,106,26,.18); color:#fff; }
.cq-btn.go { border-color:rgba(255,80,26,.7); color:#ff8a4a; }
.cq-btn.ghost { color:#a99; border-color:rgba(120,120,120,.3); }
.cq-genlist { display:none; flex-direction:column; margin:2px 0 6px; }
.cq-genlist.show { display:flex; }
.cq-gbtn { cursor:pointer; padding:6px 10px; font-size:12px; color:#dfeaff; border:1px solid rgba(120,160,220,.3); background:rgba(10,14,22,.6); margin:2px 0; display:flex; justify-content:space-between; }
.cq-gbtn:hover { background:rgba(90,140,220,.2); }
.cq-gbtn .pw { color:#8fd0ff; font-weight:700; }
#cq-gstate { font-size:11px; color:#8c857c; margin-top:2px; }
#cq-log { margin-top:auto; border-top:1px solid rgba(255,106,26,.18); padding-top:8px; max-height:150px; overflow-y:auto; font-size:11.5px; line-height:1.55; }
#cq-log .l { color:#b9b0a4; margin:2px 0; }
#cq-log .l.hot { color:#ffcf9a; }
#cq-foot { display:flex; gap:8px; margin-top:10px; }
#cq-foot .cq-btn { flex:1; margin:0; }
#cq-over { position:absolute; inset:0; display:none; flex-direction:column; align-items:center; justify-content:center;
  background:rgba(3,2,2,.9); z-index:5; text-align:center; }
#cq-over.show { display:flex; }
#cq-over .v { font-size:56px; font-weight:900; letter-spacing:.14em; }
#cq-over .v.win { color:#ffd07a; text-shadow:0 0 30px rgba(255,150,40,.6); }
#cq-over .v.lose { color:#c02020; }
#cq-over .m { color:#c9beb0; font-style:italic; margin:12px 0 22px; max-width:520px; }
`;

export class WorldMap {
  constructor(hudRoot) {
    const style = document.createElement('style'); style.textContent = CSS; document.head.appendChild(style);
    const root = document.createElement('div'); root.id = 'cq';
    root.innerHTML = `
      <div id="cq-left">
        <div id="cq-map">
          <svg id="cq-edges" viewBox="0 0 100 100" preserveAspectRatio="none"></svg>
        </div>
        <div id="cq-over"><div class="v"></div><div class="m"></div><div class="cq-btn" data-x="menu" style="min-width:220px">RETURN TO MENU</div></div>
      </div>
      <div id="cq-side">
        <h1>THE REALM</h1><div id="cq-day">DAY 1</div>
        <div class="cq-stand" id="cq-stand"></div>
        <div id="cq-detail"><div class="rd">Select a land to inspect it. Enemy factions creep in each day — hold your borders, or take the war to them.</div></div>
        <div id="cq-log"></div>
        <div id="cq-foot">
          <div class="cq-btn" data-x="hold">HOLD ⏭ (END DAY)</div>
          <div class="cq-btn ghost" data-x="menu">MENU</div>
        </div>
      </div>`;
    hudRoot.appendChild(root);
    this.root = root;
    this.map = root.querySelector('#cq-map');
    this.edges = root.querySelector('#cq-edges');
    this.stand = root.querySelector('#cq-stand');
    this.detail = root.querySelector('#cq-detail');
    this.logEl = root.querySelector('#cq-log');
    this.dayEl = root.querySelector('#cq-day');
    this.over = root.querySelector('#cq-over');
    this.nodes = {};
    this.sel = null;
    this._timer = null;
    this._hotCount = 0;

    root.querySelectorAll('[data-x]').forEach((el) => el.addEventListener('click', () => this._foot(el.dataset.x)));
  }

  setController(gm) { this.gm = gm; }

  show(conquest) {
    this.cq = conquest;
    this.map.style.backgroundImage = "url('art/worldmap.png')";
    this._buildEdges();
    this._buildNodes();
    this._render();
    this.root.classList.add('show');
    this._startClock();
  }
  hide() { this.root.classList.remove('show'); this._stopClock(); }

  _startClock() { this._stopClock(); if (this.cq.over) return; this._timer = setInterval(() => this._tick(), DAY_MS); }
  _stopClock() { if (this._timer) { clearInterval(this._timer); this._timer = null; } }

  _tick() {
    const events = this.cq.advanceDay();
    this._hotCount = events.length;
    if (this.gm) this.gm.cqPersist();
    this._render();
    if (this.cq.over) { this._stopClock(); this._showOver(); }
  }

  _foot(x) {
    if (x === 'menu') { this.hide(); this.gm.gotoMenu(); }
    else if (x === 'hold') this._tick();
  }

  // ---- static geometry ----
  _buildEdges() {
    let s = '';
    for (const [a, b] of this.cq.edges()) {
      s += `<line x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}" stroke="rgba(220,200,170,0.22)" stroke-width="0.4"/>`;
    }
    this.edges.innerHTML = s;
  }
  _buildNodes() {
    for (const id in this.nodes) this.nodes[id].remove();
    this.nodes = {};
    for (const r of this.cq.list()) {
      const el = document.createElement('div');
      el.className = 'cq-node' + (r.seat ? ' seat' : '');
      el.style.left = r.x + '%'; el.style.top = r.y + '%';
      el.innerHTML = `<div class="cq-crown"></div><div class="cq-dot"></div><div class="cq-lbl"></div><div class="cq-gar"></div>`;
      el.addEventListener('click', () => this._select(r.id));
      this.map.appendChild(el);
      this.nodes[r.id] = el;
    }
  }

  // ---- render state ----
  _render() {
    this.dayEl.textContent = 'DAY ' + this.cq.day;
    // standings
    const held = {}; for (const k in FACTIONS) held[k] = 0;
    for (const r of this.cq.list()) held[r.owner]++;
    this.stand.innerHTML = ['ashen', ...ENEMY_FACTIONS].map((k) => {
      const f = FACTIONS[k];
      return `<div class="f"><span class="cq-swatch" style="background:${f.color}"></span>${f.name.replace('The ', '')} · ${held[k]}</div>`;
    }).join('');

    for (const r of this.cq.list()) {
      const el = this.nodes[r.id];
      const f = FACTIONS[r.owner];
      el.querySelector('.cq-dot').style.background = f.color;
      el.querySelector('.cq-crown').textContent = r.seat ? '♛' : '';
      el.querySelector('.cq-lbl').textContent = r.name;
      el.querySelector('.cq-lbl').style.color = r.owner === 'ashen' ? '#dfe7f0' : '#e6ddd2';
      el.querySelector('.cq-gar').textContent = '⚔ ' + r.garrison;
      el.classList.toggle('sel', this.sel === r.id);
      // threat ring
      let ring = el.querySelector('.cq-threat'), td = el.querySelector('.cq-tdays');
      if (r.threat) {
        if (!ring) { ring = document.createElement('div'); ring.className = 'cq-threat'; el.appendChild(ring); }
        if (!td) { td = document.createElement('div'); td.className = 'cq-tdays'; el.appendChild(td); }
        ring.style.borderColor = FACTIONS[r.threat.faction].color;
        td.textContent = '⚠' + r.threat.days + 'd';
      } else { if (ring) ring.remove(); if (td) td.remove(); }
    }

    // log
    const lines = this.cq.log.slice(-16);
    this.logEl.innerHTML = lines.map((l, i) =>
      `<div class="l${i >= lines.length - this._hotCount ? ' hot' : ''}">${esc(l)}</div>`).join('');
    this.logEl.scrollTop = this.logEl.scrollHeight;

    if (this.sel) this._renderDetail(this.sel);
  }

  _select(id) { this.sel = id; this._render(); }

  _renderDetail(id) {
    const r = this.cq.region(id);
    const f = FACTIONS[r.owner];
    const acts = this.cq.actions(id);
    let h = `<div class="rn">${r.name}${r.seat ? ' ♛' : ''}</div>`;
    h += `<div class="ro" style="color:${f.color}">${f.name} · ${biomeWord(r.biome)}</div>`;
    h += `<div class="rd">${esc(r.desc)}</div>`;
    h += `<div class="rstat">Garrison <b style="color:#ffb257">${r.garrison}</b></div>`;
    if (r.threat) h += `<div class="warn">▲ ${FACTIONS[r.threat.faction].name} invading — strength ${r.threat.strength}, ${r.threat.days} day(s) to answer.</div>`;

    const btn = (x, cls, label) => `<div class="cq-btn ${cls}" data-act="${x}">${label}</div>`;
    if (acts.march && r.owner === 'ashen') h += btn('defend', 'go', '⚔ MARCH TO DEFEND');
    if (acts.assault) h += btn('assault', 'go', '⚔ MARCH TO ASSAULT');
    if (acts.dispatch) {
      h += btn('raise', '', '⚑ RAISE AN ARMY ▾');
      h += `<div class="cq-genlist" id="cq-genlist">` +
        this.cq.readyGenerals().map((g) => `<div class="cq-gbtn" data-gen="${g.name}">${g.merc ? '⚑ ' : ''}${g.name}<span class="pw">▪ ${g.power}</span></div>`).join('') +
        `</div>`;
    }
    if (acts.walk) h += btn('walk', 'ghost', '🏰 RIDE THROUGH YOUR LANDS');
    if (!acts.march && !acts.assault && !acts.walk) h += `<div class="rstat" style="color:#8c857c">Beyond your reach. Take a bordering land first.</div>`;
    // unavailable-generals hint (marching / resting / wounded / deserted)
    const away = Object.values(this.cq.generals).filter((g) => g.status !== 'ready');
    const label = (g) => g.status === 'marching' ? `marching (${g.eta}d)` : g.status === 'wounded' ? `wounded (${g.cd}d)` : g.status === 'gone' ? 'deserted' : `resting (${g.cd}d)`;
    if (away.length) h += `<div id="cq-gstate">${away.map((g) => `${g.name}: ${label(g)}`).join(' · ')}</div>`;

    this.detail.innerHTML = h;
    this.detail.querySelectorAll('[data-act]').forEach((el) => el.addEventListener('click', () => this._act(el.dataset.act, id)));
    const rl = this.detail.querySelector('[data-act="raise"]');
    if (rl) rl.addEventListener('click', () => this.detail.querySelector('#cq-genlist').classList.toggle('show'));
    this.detail.querySelectorAll('[data-gen]').forEach((el) => el.addEventListener('click', () => {
      if (this.cq.dispatch(id, el.dataset.gen)) { this.gm.cqPersist(); this._render(); }
    }));
  }

  _act(a, id) {
    if (a === 'defend') { this.hide(); this.gm.cqMarch(id, 'defend'); }
    else if (a === 'assault') { this.hide(); this.gm.cqMarch(id, 'assault'); }
    else if (a === 'walk') { this.hide(); this.gm.cqWalk(id); }
  }

  _showOver() {
    const win = this.cq.over === 'win';
    this.over.querySelector('.v').className = 'v ' + (win ? 'win' : 'lose');
    this.over.querySelector('.v').textContent = win ? 'THE REALM IS UNITED' : 'THE HOST IS BROKEN';
    this.over.querySelector('.m').textContent = win
      ? 'Every banner in the realm flies the sword-and-star. The war is won, Commander.'
      : 'Redmarch has fallen and the Ashen Host is scattered. The realm burns.';
    this.over.classList.add('show');
  }
}

function esc(s) { return String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])); }
function biomeWord(b) {
  return ({ ashlands: 'Ash-Flats', emberwood: 'Emberwood', frostmark: 'Frostmark', saltmere: 'Sea-Coast', goldfen: 'Farmland', ironpeak: 'Highlands', duskmarsh: 'Marsh' })[b] || b;
}
